import React from 'react';
import PropTypes from 'prop-types';
import {
  FileText,
  ChatCircle,
  Phone,
  File,
  Calendar,
  Clock,
} from '@phosphor-icons/react';
import Typography from '../Typography';
import './activityFeed.scss';

// Activity type configurations with icons and colors
const ACTIVITY_CONFIG = {
  referral_received: {
    icon: FileText,
    color: 'purple',
    title: 'Referral Received',
  },
  message_sent: {
    icon: ChatCircle,
    color: 'blue',
    title: 'Message Sent',
  },
  message_received: {
    icon: ChatCircle,
    color: 'blue',
    title: 'Message Received',
  },
  patient_contacted: {
    icon: Phone,
    color: 'green',
    title: 'Patient Contacted',
  },
  document_uploaded: {
    icon: File,
    color: 'pink',
    title: 'Document Uploaded',
  },
  appointment_scheduled: {
    icon: Calendar,
    color: 'purple',
    title: 'Appointment Scheduled',
  },
  status_change: {
    icon: FileText,
    color: 'purple',
    title: 'Status Updated',
  },
};

const ActivityFeed = ({ activities, lastUpdated }) => {
  const formatTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Just now';
    const now = new Date();
    const updated = new Date(lastUpdated);
    const diffMs = now - updated;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return formatTimestamp(lastUpdated);
  };

  const getActivityConfig = (type) => {
    return ACTIVITY_CONFIG[type] || ACTIVITY_CONFIG.status_change;
  };

  if (!activities || activities.length === 0) {
    return (
      <div className="activity-feed">
        <div className="activity-feed__header">
          <div className="activity-feed__header-left">
            <Typography variant="text" size="medium" bold>
              Activity Timeline
            </Typography>
            <Typography variant="text" size="small" className="activity-feed__subtitle">
              Real-time log of all referral activity
            </Typography>
          </div>
        </div>
        <div className="activity-feed__empty">
          <Typography variant="text" size="small">
            No activity recorded yet
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      <div className="activity-feed__header">
        <div className="activity-feed__header-left">
          <Typography variant="text" size="medium" bold>
            Activity Timeline
          </Typography>
          <Typography variant="text" size="small" className="activity-feed__subtitle">
            Real-time log of all referral activity
          </Typography>
        </div>
        <div className="activity-feed__last-updated">
          <Clock size={14} />
          <Typography variant="text" size="small">
            Last updated: {formatLastUpdated()}
          </Typography>
        </div>
      </div>

      <div className="activity-feed__timeline">
        {activities.map((activity, index) => {
          const config = getActivityConfig(activity.type);
          const IconComponent = config.icon;

          return (
            <div key={activity.id || index} className="activity-feed__item">
              <div className="activity-feed__icon-column">
                <div className={`activity-feed__icon activity-feed__icon--${config.color}`}>
                  <IconComponent size={18} />
                </div>
              </div>

              <div className="activity-feed__content">
                <div className="activity-feed__content-header">
                  <Typography variant="text" bold className="activity-feed__title">
                    {activity.title || config.title}
                  </Typography>
                  <Typography variant="text" size="small" className="activity-feed__timestamp">
                    {formatTimestamp(activity.timestamp)}
                  </Typography>
                </div>
                <Typography variant="text" size="small" className="activity-feed__description">
                  {activity.description}
                </Typography>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ActivityFeed.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string.isRequired,
      title: PropTypes.string,
      description: PropTypes.string.isRequired,
      timestamp: PropTypes.string.isRequired,
    })
  ),
  lastUpdated: PropTypes.string,
};

ActivityFeed.defaultProps = {
  activities: [],
  lastUpdated: null,
};

export default ActivityFeed;
